import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  Users,
  Map,
  Wand2,
  Terminal,
  Globe,
  Bot,
  Shield,
  Code,
  Search,
  FileText,
  Compass,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface NpcCard {
  name: string;
  tagline: string;
  status: 'IDLE' | 'BUSY';
  color: string;
  seed: string;
}

interface FeatureItem {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
}

interface AgentTool {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  detail: string;
}

interface LabelItem {
  label: string;
}

// ── Data ──────────────────────────────────────────────────────────────────────

export const NPC_CARDS: NpcCard[] = [
  {
    name: 'Ember',
    tagline: 'Need a repair, or do you just want honest advice?',
    status: 'IDLE',
    color: '#0ea5e9',
    seed: 'ember',
  },
  {
    name: 'Lyra',
    tagline: 'A question well-asked can open ten doors. What are you\u2026',
    status: 'IDLE',
    color: '#2563eb',
    seed: 'lyra',
  },
  {
    name: 'Nyx',
    tagline: 'Some answers are doors. Some are traps. Which are you\u2026',
    status: 'IDLE',
    color: '#06b6d4',
    seed: 'nyx',
  },
  {
    name: 'Rusty',
    tagline: 'Pull up a stool. I can spare a story if you can spare a minute.',
    status: 'IDLE',
    color: '#38bdf8',
    seed: 'rusty',
  },
  {
    name: 'Whisper',
    tagline: 'The map is not the path, traveler. Which way is your\u2026',
    status: 'IDLE',
    color: '#22d3ee',
    seed: 'whisper',
  },
];

export const FEATURES: FeatureItem[] = [
  {
    icon: Map,
    title: 'Multi-Zone World',
    desc: 'WASD movement across village, forest, and library zones with real-time collision, camera follow, and zone-aware ambient audio.',
  },
  {
    icon: Bot,
    title: 'Living NPCs',
    desc: '5 built-in characters with unique personalities, long-term memory, and patrol routes. They remember you across every session.',
  },
  {
    icon: Wand2,
    title: 'AI Level Generation',
    desc: 'Describe a scene in plain English. A 4-stage Gemini pipeline builds the full tile layout, layer by layer.',
  },
  {
    icon: Terminal,
    title: 'Autonomous Agents',
    desc: 'User-created NPCs with real tool access \u2014 web search, Python execution, file generation \u2014 running in secure sandboxes.',
  },
  {
    icon: Users,
    title: 'Multiplayer Social',
    desc: 'Proximity chat, friend lists, minimap markers, and shareable profile links. Real-time presence with avatar interpolation.',
  },
  {
    icon: Shield,
    title: 'Enterprise Grade',
    desc: 'Firebase-backed auth, per-user token budgets, server-side NPC limits, and multi-model failover across Gemini, OpenRouter, and Anthropic.',
  },
];

export const AGENT_TOOLS: AgentTool[] = [
  {
    icon: Search,
    label: 'Web Intelligence',
    detail: 'Live Google News search piped into NPC responses',
  },
  {
    icon: FileText,
    label: 'Document Creation',
    detail: 'Generate PPTX, XLSX, Word, and PDF via skill sets',
  },
  {
    icon: Code,
    label: 'Code Execution',
    detail: 'Python sandboxes with continuation & timeout support',
  },
  {
    icon: Globe,
    label: 'Multi-Model',
    detail: 'Gemini \u00b7 Claude 3.5 Sonnet \u00b7 OpenRouter \u2014 swap anytime',
  },
];

export const PLATFORM_LINKS: LabelItem[] = [
  { label: 'Live Map' },
  { label: 'Agent Runtime' },
  { label: 'NPC Creator' },
  { label: 'Level Editor' },
];

export const RESOURCE_LINKS: LabelItem[] = [
  { label: 'Documentation' },
  { label: 'API Reference' },
  { label: 'Token Budget' },
  { label: 'Community Skills' },
];

export const EDITOR_BULLETS: LabelItem[] = [
  { label: 'Multi-layer system \u2014 Floor, Walls, Furniture, Decoration' },
  { label: 'Traversal mask painting for collision control' },
  { label: 'PNG export for background and mask layers' },
  { label: 'Real-time canvas preview with movement simulation' },
];

export const NPC_FEATURE_BULLETS: LabelItem[] = [
  { label: 'LTM summarization keeps memories across sessions' },
  { label: 'Proximity detection \u2014 approach to trigger interaction' },
  { label: 'Patrol + pause-on-proximity behavior' },
  { label: 'Per-NPC conversation history in Firestore' },
  { label: 'Multi-model LLM backend \u2014 Gemini, Claude, OpenRouter' },
];

export const ZONE_LABELS: LabelItem[] = [
  { label: '\ud83c\udfe0 Village Square' },
  { label: '\ud83c\udf32 Forest Wilds' },
  { label: '\ud83d\udcda Ancient Library' },
  { label: '\ud83c\udfd6\ufe0f Shore Outpost' },
  { label: '\u2694\ufe0f Ruins of Kael' },
  { label: '\ud83c\udf3f Verdant Crossing' },
  { label: '\ud83e\uddd9 Arcane Tower' },
];

export const NAV_ITEMS: LabelItem[] = [
  { label: 'Explore' },
  { label: 'NPCs' },
  { label: 'Agents' },
  { label: 'Editor' },
];

export const SOCIAL_PROOF_SEEDS: LabelItem[] = [
  { label: '10' },
  { label: '22' },
  { label: '35' },
  { label: '48' },
  { label: '61' },
];

export const STAR_KEYS: LabelItem[] = [
  { label: 's1' },
  { label: 's2' },
  { label: 's3' },
  { label: 's4' },
  { label: 's5' },
];

export const SOCIAL_ICONS = [
  { Icon: Globe, name: 'Website' },
  { Icon: MessageSquare, name: 'Discord' },
  { Icon: Users, name: 'Community' },
];

export const LAYER_LABELS: LabelItem[] = [
  { label: 'Floor' },
  { label: 'Walls' },
  { label: 'Furniture' },
  { label: 'Deco' },
];

const SIDEBAR_TABS: LabelItem[] = [{ label: 'WORLD' }, { label: 'SOCIAL' }, { label: 'HISTORY' }];

// ── Game UI Mock ──────────────────────────────────────────────────────────────

export function GameMock(): JSX.Element {
  const [activeNpc, setActiveNpc] = useState<string | null>(null);

  return (
    <div className="w-full overflow-hidden rounded-2xl border border-[#1e3a5f]/60 bg-[#071428]/80 shadow-[0_0_80px_rgba(14,165,233,0.18)] backdrop-blur-xl">
      {/* Title bar */}
      <div className="flex items-center gap-3 border-b border-[#1e3a5f]/60 bg-[#050b1a]/90 px-4 py-2.5">
        <div className="flex gap-1.5">
          <div className="h-3 w-3 rounded-full bg-[#0ea5e9]/70" />
          <div className="h-3 w-3 rounded-full bg-[#22d3ee]/70" />
          <div className="h-3 w-3 rounded-full bg-[#38bdf8]/70" />
        </div>
        <span className="ml-1 font-mono text-[11px] text-sky-400/50">waysprite.app</span>
      </div>

      <div className="flex" style={{ height: '420px' }}>
        {/* Game canvas */}
        <div className="relative flex-1 overflow-hidden bg-[#060f1e]">
          <img
            src="https://images.unsplash.com/photo-1578662996442-48f60103fc96?q=80&w=1200&auto=format&fit=crop"
            alt="Top-down pixel RPG game world"
            className="h-full w-full object-cover opacity-40 mix-blend-luminosity"
          />
          <div
            className="absolute inset-0 opacity-15"
            style={{
              backgroundImage:
                'linear-gradient(rgba(34,211,238,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.6) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          <div className="absolute left-3 top-3 flex items-center gap-2 rounded-lg border border-[#22d3ee]/25 bg-[#071428]/80 px-2.5 py-1.5 backdrop-blur-md">
            <div className="h-2 w-2 animate-pulse rounded-full bg-[#22d3ee]" />
            <span className="font-mono text-[10px] tracking-wide text-sky-200">
              VILLAGE &middot; ZONE 1
            </span>
          </div>
          <div className="absolute bottom-3 left-3 rounded-lg border border-[#1e3a5f]/60 bg-[#071428]/80 p-1.5 backdrop-blur-md">
            <div className="mb-1 px-0.5 font-bold uppercase tracking-widest text-[8px] text-cyan-400">
              Minimap
            </div>
            <div className="relative h-12 w-16 overflow-hidden rounded bg-[#0a1a30]">
              <div
                className="absolute inset-0 opacity-50"
                style={{
                  backgroundImage:
                    'linear-gradient(45deg, #060f1e 25%, #0d2240 25%, #0d2240 50%, #060f1e 50%, #060f1e 75%, #0d2240 75%)',
                  backgroundSize: '4px 4px',
                }}
              />
              <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0ea5e9] shadow-[0_0_6px_rgba(14,165,233,0.9)]" />
            </div>
          </div>
          <motion.div
            animate={{ x: [0, 30, 60, 40, 0], y: [0, 20, 0, -20, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
            className="absolute left-1/3 top-1/3"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-sm border-2 border-[#38bdf8] bg-[#0ea5e9] shadow-lg">
              <div className="h-3 w-3 rounded-sm bg-[#050b1a]" />
            </div>
            <div className="mt-0.5 rounded bg-[#050b1a]/70 px-1 text-center text-[8px] font-bold text-white">
              Max
            </div>
          </motion.div>
          <div className="absolute right-1/3 top-2/5">
            <motion.div
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 2.5, repeat: Infinity }}
              className="h-7 w-7 rounded-sm border-2 border-[#22d3ee] bg-[#06b6d4] shadow-lg"
            />
            <div className="mt-0.5 rounded bg-[#050b1a]/70 px-1 text-center text-[8px] font-bold text-white">
              Lyra
            </div>
          </div>
          <div className="absolute bottom-3 right-3 rounded-lg border border-[#0ea5e9]/30 bg-[#071428]/80 px-2.5 py-1.5 backdrop-blur-md">
            <span className="font-mono text-[10px] text-[#38bdf8]">Press </span>
            <span className="rounded border border-[#0ea5e9]/30 bg-[#0a1a30] px-1 font-mono text-[10px] font-bold text-white">
              E
            </span>
            <span className="font-mono text-[10px] text-[#38bdf8]"> to Chat</span>
          </div>
        </div>

        {/* Sidebar */}
        <div className="flex w-56 flex-shrink-0 flex-col border-l border-[#1e3a5f]/60 bg-[#071428]/90 backdrop-blur-md">
          <div className="flex items-center justify-between border-b border-[#1e3a5f]/60 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded bg-[#0ea5e9]">
                <span className="text-[9px] font-black text-white">M</span>
              </div>
              <span className="text-[12px] font-bold text-white">Max</span>
            </div>
            <div className="flex gap-1">
              <div className="flex h-5 w-5 items-center justify-center rounded bg-[#0a1a30]">
                <Users className="h-2.5 w-2.5 text-sky-400" />
              </div>
              <div className="flex h-5 w-5 items-center justify-center rounded bg-[#0a1a30]">
                <Compass className="h-2.5 w-2.5 text-sky-400" />
              </div>
            </div>
          </div>
          <div className="flex border-b border-[#1e3a5f]/60">
            {SIDEBAR_TABS.map((tab) => (
              <button
                key={tab.label}
                className={`flex-1 py-2 text-[9px] font-bold tracking-widest transition-colors ${
                  tab.label === 'WORLD'
                    ? 'border-b-2 border-[#0ea5e9] text-[#22d3ee]'
                    : 'text-sky-700 hover:text-sky-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between px-3 py-2">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-sky-400">
                Characters
              </div>
              <div className="text-[9px] text-sky-700">0 busy / 5 Characters</div>
            </div>
            <button className="rounded border border-[#0ea5e9]/30 bg-[#0ea5e9]/15 px-1.5 py-0.5 text-[8px] font-bold text-[#38bdf8]">
              Council
            </button>
          </div>
          <div className="flex-1 space-y-1.5 overflow-y-auto px-2 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {NPC_CARDS.map((npc) => (
              <button
                key={npc.seed}
                onClick={() => setActiveNpc(activeNpc === npc.name ? null : npc.name)}
                className={`w-full rounded-lg border p-2 text-left transition-all ${
                  activeNpc === npc.name
                    ? 'border-[#0ea5e9]/50 bg-[#0a2040]'
                    : 'border-[#1e3a5f]/50 bg-[#050b1a] hover:border-[#1e5a8f]/60'
                }`}
              >
                <div className="mb-1 flex items-center gap-2">
                  <div
                    className="h-6 w-6 flex-shrink-0 overflow-hidden rounded"
                    style={{ background: npc.color }}
                  >
                    <img
                      src={`https://api.dicebear.com/7.x/pixel-art/svg?seed=${npc.seed}`}
                      alt={`${npc.name} avatar`}
                      className="h-full w-full"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-bold leading-none text-white">{npc.name}</div>
                    <div className="mt-0.5 flex items-center gap-1">
                      <div className="h-1.5 w-1.5 rounded-full bg-[#22d3ee]" />
                      <span className="text-[8px] font-bold text-[#22d3ee]">{npc.status}</span>
                    </div>
                  </div>
                </div>
                <p className="line-clamp-2 text-[8px] leading-tight text-sky-600">{npc.tagline}</p>
                {activeNpc === npc.name && (
                  <div className="mt-1.5 flex gap-1">
                    <button
                      className="flex-1 rounded border border-[#1e3a5f] bg-[#0a2040] py-1 text-center text-[8px] font-bold text-sky-200"
                      type="button"
                    >
                      Chat
                    </button>
                    <button
                      className="flex-1 rounded border border-[#1e3a5f] bg-[#0a2040] py-1 text-center text-[8px] font-bold text-sky-200"
                      type="button"
                    >
                      Task
                    </button>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
